import { useState } from 'react';
import { Badge } from '../shared/components/Badge';
import { Tooltip } from '../shared/components/Tooltip';
import { useProducts } from '../shared/hooks/useProducts';
import { getStatusInfo } from '../shared/utils/inventoryCalculations';
import { ProductWithCalculations } from '../shared/types/inventory';
import { ProductDetailModal } from '../components/ProductDetailModal';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { PageTitle} from '../../../../_metronic/layout/core'

export const TablePage = () => {
  const { products, loading, error, refetch } = useProducts();
  const [selectedProduct, setSelectedProduct] = useState<ProductWithCalculations | null>(null);

  const formatNumber = (num: number, decimals: number = 2) => {
    return num.toFixed(decimals);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          <p className="text-gray-500 text-sm">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-gray-900 font-semibold">Erro ao carregar dados</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
        <PageTitle>Tabela de Reposição</PageTitle>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tabela de Reposição</h1>
          <p className="text-gray-600">
            Analise detalhada dos produtos com sugestoes de pedido baseadas em dados
          </p>
        </div>
        <button
          onClick={refetch}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Quantidade atual em estoque">
                    <span className="cursor-help">Estoque Atual</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Consumo Medio Diario: Vendas no Periodo / Dias do Periodo">
                    <span className="cursor-help">CMD</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Safety Stock: Estoque de Seguranca = CMD x Lead Time">
                    <span className="cursor-help">SS</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Reorder Point: Ponto de Pedido = DDLT + SS">
                    <span className="cursor-help">ROP</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Dias de cobertura: Estoque Atual / CMD">
                    <span className="cursor-help">Cobertura (dias)</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Nivel ideal de estoque: DDLT + SS + (Cobertura Alvo x CMD)">
                    <span className="cursor-help">Estoque Alvo</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  <Tooltip content="Quantidade sugerida para reposicao: MAX(0, Estoque Alvo - Estoque Atual)">
                    <span className="cursor-help">Pedido Sugerido</span>
                  </Tooltip>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => {
                const statusInfo = getStatusInfo(product.status);
                return (
                  <tr
                    key={product.id}
                    className="hover:bg-blue-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <td className="px-4 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{product.nome}</div>
                        <div className="text-sm text-gray-500">EAN: {product.ean}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-gray-900">
                      {product.estoqueAtual}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatNumber(product.cmd)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatNumber(product.ss)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatNumber(product.rop)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatNumber(product.cobertura)}
                    </td>
                    <td className="px-4 py-4 text-right text-gray-900">
                      {formatNumber(product.estoqueAlvo)}
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-blue-600">
                      {formatNumber(product.pedidoSugerido, 0)}
                    </td>
                    <td className="px-4 py-4">
                      <Badge
                        label={statusInfo.label}
                        color={statusInfo.color}
                        bgColor={statusInfo.bgColor}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Dica:</strong> Passe o mouse sobre os titulos das colunas para ver a explicacao de cada metrica. Clique em qualquer produto para ver a visualizacao detalhada.
        </p>
      </div>

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};
